import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaLock } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  buildTimingsAzurePartial,
  canEditMeasurementSchedule,
  INSTALLATION_DETAIL_STALE_KEY,
  INSTALLATION_LIST_STALE_KEY,
  isValidCronLine,
  resolveAddressFromSources,
} from "./installationConfigUtils";
import {
  buildInitialCronState,
  resolveActiveTimingsForSave,
} from "./installationScheduleModeUtils";
import {
  resetImageReadingsToDefaults,
  updateCameraStatus,
  updateSiteProperties,
  writeAzureConfig,
} from "./installationApi";
import { buildDefaultImageReadingsCronState } from "./installationImageReadingsDefaults";
import { isMobileAkanthasAccount } from "../mobileModuleUtils";
import InstallationAddressSection from "./InstallationAddressSection";
import InstallationCronSection from "./InstallationCronSection";
import InstallationImageReadingsResetSection from "./InstallationImageReadingsResetSection";
import InstallationConnectivitySection from "./InstallationConnectivitySection";
import InstallationStateSection from "./InstallationStateSection";
import InstallationBrightnessSection from "./InstallationBrightnessSection";
import InstallationVisualizationSidesSection from "./InstallationVisualizationSidesSection";

export { buildInitialCronState } from "./installationScheduleModeUtils";

const InstallationConfigPanel = ({
  api,
  token,
  routeCameraId,
  leftCamera,
  rightCamera,
  hasRightCamera,
  configData,
  setConfigData,
  siteEditId,
  cameraListItem,
  reload,
  batteryLevel,
  networkDetails,
  simProvider,
  batteryLoading,
}) => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);

  const { addressId: initialAddressId, addressName: initialAddressName } =
    useMemo(
      () => resolveAddressFromSources(leftCamera, cameraListItem),
      [leftCamera, cameraListItem],
    );

  const [addressId, setAddressId] = useState(initialAddressId);
  const [addressName, setAddressName] = useState(initialAddressName);
  const [cameraStatus, setCameraStatus] = useState(
    () => leftCamera?.status || cameraListItem?.status || "",
  );
  const [cronState, setCronState] = useState(() =>
    buildInitialCronState(configData),
  );
  const persistedSnapshotRef = useRef(null);

  const cronEditable = canEditMeasurementSchedule(leftCamera?.codeVersion);
  const showAdminInstallationTools = isMobileAkanthasAccount();

  useEffect(() => {
    const resolved = resolveAddressFromSources(leftCamera, cameraListItem);
    const nextCronState = buildInitialCronState(configData);
    const persisted = persistedSnapshotRef.current;

    const nextAddressId = resolved.addressId ?? persisted?.addressId ?? null;
    const nextAddressName =
      resolved.addressName?.trim() ||
      (persisted?.addressId === nextAddressId ? persisted?.addressName : "") ||
      "";

    const nextStatus = leftCamera?.status || cameraListItem?.status || "";

    setAddressId(nextAddressId);
    setAddressName(nextAddressName);
    setCameraStatus(nextStatus);
    setCronState(nextCronState);
    persistedSnapshotRef.current = {
      addressId: nextAddressId,
      addressName: nextAddressName,
      status: nextStatus,
      cronState: nextCronState,
    };
  }, [leftCamera, configData, cameraListItem]);

  const saveStatus = useCallback(
    async (nextStatus) => {
      if (
        nextStatus === persistedSnapshotRef.current?.status ||
        !leftCamera?._id
      ) {
        return true;
      }

      setSaving(true);
      try {
        await updateCameraStatus(api, token, routeCameraId, nextStatus);
        persistedSnapshotRef.current = {
          ...persistedSnapshotRef.current,
          status: nextStatus,
        };
        setCameraStatus(nextStatus);
        sessionStorage.setItem(INSTALLATION_LIST_STALE_KEY, "1");
        sessionStorage.setItem(INSTALLATION_DETAIL_STALE_KEY, "1");
        toast.success(t("installation_saved"));
        await reload({ silent: true });
        return true;
      } catch {
        toast.error(t("installation_save_failed"));
        return false;
      } finally {
        setSaving(false);
      }
    },
    [api, token, routeCameraId, leftCamera?._id, t, reload],
  );

  const saveAddress = useCallback(
    async (nextAddressId, nextAddressName) => {
      if (
        nextAddressId === persistedSnapshotRef.current?.addressId ||
        !siteEditId ||
        !leftCamera?.corporationId
      ) {
        return true;
      }

      setSaving(true);
      try {
        await updateSiteProperties(api, token, {
          _id: siteEditId,
          corporationId: leftCamera.corporationId,
          addressId: nextAddressId,
        });
        persistedSnapshotRef.current = {
          ...persistedSnapshotRef.current,
          addressId: nextAddressId,
          addressName: nextAddressName,
        };
        setAddressId(nextAddressId);
        setAddressName(nextAddressName);
        sessionStorage.setItem(INSTALLATION_LIST_STALE_KEY, "1");
        toast.success(t("installation_saved"));
        await reload({ silent: true });
        return true;
      } catch {
        toast.error(t("installation_save_failed"));
        return false;
      } finally {
        setSaving(false);
      }
    },
    [api, token, siteEditId, leftCamera?.corporationId, t, reload],
  );

  const saveCron = useCallback(
    async (nextCronState) => {
      if (!cronEditable) {
        return true;
      }

      if (
        JSON.stringify(nextCronState) ===
        JSON.stringify(persistedSnapshotRef.current?.cronState)
      ) {
        return true;
      }

      const activeTimings = resolveActiveTimingsForSave(nextCronState);
      const invalid = activeTimings.find((line) => !isValidCronLine(line));
      if (invalid) {
        toast.error(t("installation_cron_invalid"));
        return false;
      }

      setSaving(true);
      try {
        const azurePartial = buildTimingsAzurePartial(
          configData,
          activeTimings,
          nextCronState.timingsSave,
        );
        const { output } = await writeAzureConfig(
          api,
          token,
          routeCameraId,
          configData,
          azurePartial,
        );
        setConfigData(output);
        setCronState(nextCronState);
        persistedSnapshotRef.current = {
          ...persistedSnapshotRef.current,
          cronState: nextCronState,
        };
        sessionStorage.setItem(INSTALLATION_LIST_STALE_KEY, "1");
        toast.success(t("installation_saved"));
        await reload({ silent: true });
        return true;
      } catch {
        toast.error(t("installation_save_failed"));
        return false;
      } finally {
        setSaving(false);
      }
    },
    [
      api,
      token,
      routeCameraId,
      configData,
      setConfigData,
      cronEditable,
      t,
      reload,
    ],
  );

  const handleResetImageReadings = useCallback(async () => {
    if (!cronEditable) {
      return false;
    }
    setSaving(true);
    try {
      const { output } = await resetImageReadingsToDefaults(
        api,
        token,
        routeCameraId,
        configData,
        leftCamera,
        rightCamera,
      );
      const nextCronState = buildDefaultImageReadingsCronState();
      setConfigData(output);
      setCronState(nextCronState);
      persistedSnapshotRef.current = {
        ...persistedSnapshotRef.current,
        cronState: nextCronState,
      };
      sessionStorage.setItem(INSTALLATION_LIST_STALE_KEY, "1");
      sessionStorage.setItem(INSTALLATION_DETAIL_STALE_KEY, "1");
      toast.success(t("installation_saved"));
      await reload({ silent: true });
      return true;
    } catch {
      toast.error(t("installation_save_failed"));
      return false;
    } finally {
      setSaving(false);
    }
  }, [
    api,
    token,
    routeCameraId,
    configData,
    leftCamera,
    rightCamera,
    setConfigData,
    cronEditable,
    t,
    reload,
  ]);

  return (
    <div className="installation-config-panel">
      <div className="installation-settings-list">
        <div className="installation-settings-group">
          <h3 className="installation-settings-group-title">
            {t("installation_section_state")}
          </h3>
          <div className="installation-settings-card">
            <InstallationStateSection
              routeCameraId={routeCameraId}
              status={cameraStatus}
              onStatusSave={saveStatus}
              batteryLevel={batteryLevel}
              loading={batteryLoading}
              disabled={saving}
              saving={saving}
            />
          </div>
        </div>

        <div className="installation-settings-group">
          <h3 className="installation-settings-group-title">
            {t("installation_section_connectivity")}
          </h3>
          <div className="installation-settings-card">
            <InstallationConnectivitySection
              routeCameraId={routeCameraId}
              networkDetails={networkDetails}
              simProvider={simProvider}
              configData={configData}
              loading={batteryLoading}
              disabled={saving}
            />
          </div>
        </div>

        <div className="installation-settings-group">
          <h3 className="installation-settings-group-title">
            {t("installation_section_site")}
          </h3>
          <div className="installation-settings-card">
            <InstallationAddressSection
              corporationId={leftCamera?.corporationId}
              addressId={addressId}
              addressName={addressName}
              onAddressSave={saveAddress}
              disabled={saving}
              saving={saving}
            />
          </div>
        </div>

        <div className="installation-settings-group">
          <h3 className="installation-settings-group-title">
            {t("installation_section_image_readings")}
          </h3>
          <div className="installation-settings-card">
            {showAdminInstallationTools && (
              <InstallationCronSection
                routeCameraId={routeCameraId}
                codeVersion={leftCamera?.codeVersion}
                cronState={cronState}
                onCronSave={saveCron}
                openDetailsPage
                disabled={saving}
                saving={saving}
                adminOnly
              />
            )}
            {showAdminInstallationTools && (
              <InstallationBrightnessSection
                routeCameraId={routeCameraId}
                configData={configData}
                disabled={saving}
                divided
                adminOnly
              />
            )}
            <InstallationVisualizationSidesSection
              routeCameraId={routeCameraId}
              configData={configData}
              hasRightCamera={hasRightCamera}
              disabled={saving}
              divided
            />
            {showAdminInstallationTools && (
              <InstallationImageReadingsResetSection
                disabled={saving || !cronEditable}
                saving={saving}
                onConfirm={handleResetImageReadings}
                adminOnly
                divided
              />
            )}
          </div>
        </div>
        {showAdminInstallationTools && (
          <p className="installation-admin-locked-legend">
            <FaLock
              className="installation-admin-locked-legend-icon"
              aria-hidden
            />
            <span>{t("installation_admin_locked_tabs_legend")}</span>
          </p>
        )}
      </div>
    </div>
  );
};

export default InstallationConfigPanel;
